import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { authService, AuthResponse } from '../services/authService';
import { APIError } from '../services/api';

// Types
interface User {
  id: string;
  email: string;
  fullName: string;
  avatar?: string;
  subscription?: 'free' | 'premium' | 'pro';
  enabledFeatures?: string[];
  joinedDate: string;
  authProvider?: 'password' | 'google';
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  signup: (userData: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGithub: () => Promise<void>;
  clearError: () => void;
}

interface SignupData {
  fullName: string;
  email: string;
  password: string;
}

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Helper function to convert AuthResponse to User
const convertAuthResponseToUser = (response: AuthResponse): User => ({
  id: response.user.id,
  email: response.user.email,
  fullName: response.user.fullName,
  avatar: response.user.avatar,
  subscription: response.user.subscription as 'free' | 'premium' | 'pro',
  enabledFeatures: response.user.enabledFeatures || [],
  joinedDate: response.user.joinedDate,
  authProvider: response.user.authProvider || 'password',
});

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing auth token on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        if (authService.isAuthenticated()) {
          const token = authService.getAccessToken();

          // Check if it's the mock token
          if (token === 'mock-demo-token') {
            const userData = localStorage.getItem('user');
            if (userData) {
              const user = JSON.parse(userData);
              dispatch({ type: 'AUTH_SUCCESS', payload: user });
              return;
            }
          }

          try {
            // Try to verify token with server by getting profile
            const userData = await authService.getProfile();
            const user: User = {
              id: userData.id,
              email: userData.email,
              fullName: userData.fullName,
              avatar: userData.avatar,
              subscription: userData.subscription as 'free' | 'premium' | 'pro',
              enabledFeatures: userData.enabledFeatures || [],
              joinedDate: userData.createdAt,
              authProvider: userData.googleId ? 'google' : 'password',
            };
            dispatch({ type: 'AUTH_SUCCESS', payload: user });
          } catch (apiError) {
            // If API fails but we have mock token, keep the mock user
            if (token === 'mock-demo-token') {
              const userData = localStorage.getItem('user');
              if (userData) {
                const user = JSON.parse(userData);
                dispatch({ type: 'AUTH_SUCCESS', payload: user });
                return;
              }
            }
            throw apiError;
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Clear invalid tokens
        authService.logout();
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_START' });

      // Mock authentication for demo purposes (when backend is not available)
      if (email === 'demo@letsboostup.com' && password === 'demo123') {
        const mockUser: User = {
          id: 'demo-user-1',
          email: 'demo@letsboostup.com',
          fullName: 'Demo User',
          avatar: '',
          subscription: 'free',
          joinedDate: new Date().toISOString(),
        };

        // Store mock token for demo
        localStorage.setItem('access_token', 'mock-demo-token');
        localStorage.setItem('user', JSON.stringify(mockUser));
        // Sync token to cookie for middleware auth check
        document.cookie = `auth_token=mock-demo-token; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;

        dispatch({ type: 'AUTH_SUCCESS', payload: mockUser });
        return;
      }

      // Try real API call
      const response = await authService.login({ email, password });
      const user = convertAuthResponseToUser(response);
      // Sync token to cookie for middleware auth check
      const token = localStorage.getItem('access_token');
      if (token) {
        document.cookie = `auth_token=${token}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
      }

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error) {
      let message = 'Login failed';
      if (error instanceof APIError) {
        message = error.message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      dispatch({ type: 'AUTH_FAILURE', payload: message });
    }
  };

  const signup = async (userData: SignupData) => {
    try {
      dispatch({ type: 'AUTH_START' });
      console.log('🚀 Starting signup process...', { email: userData.email });

      const response = await authService.register({
        fullName: userData.fullName,
        email: userData.email,
        password: userData.password,
      });
      console.log('✅ Signup successful:', response);

      const user = convertAuthResponseToUser(response);
      console.log('✅ User converted:', user);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
      console.log('✅ Auth state updated, user should be redirected');
    } catch (error) {
      console.error('❌ Signup error:', error);
      let message = 'Signup failed';
      if (error instanceof APIError) {
        console.error('❌ API Error:', { status: error.status, message: error.message, data: error.data });
        // Show more specific error messages based on status code
        if (error.status === 409) {
          message = error.message || error.data?.message || 'An account with this email already exists. Please login instead.';
        } else if (error.status === 400) {
          message = error.data?.message || error.message || 'Invalid signup data. Please check your information.';
        } else if (error.status === 500) {
          message = error.data?.message || error.message || 'Server error. Please try again later.';
        } else {
          message = error.message || error.data?.message || `Signup failed (${error.status})`;
        }
      } else if (error instanceof Error) {
        message = error.message;
      }
      dispatch({ type: 'AUTH_FAILURE', payload: message });
    }
  };

  const loginWithGoogle = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'Google sign-in is not configured.' });
      return;
    }

    // Redirect the current page to Google's OAuth consent screen.
    // After the user picks an account, Google redirects back to our origin
    // with the access_token in the URL hash. App.tsx handles the callback.
    const redirectUri = window.location.origin;
    const scope = 'openid email profile';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=token` +
      `&scope=${encodeURIComponent(scope)}` +
      `&prompt=select_account`;

    window.location.href = authUrl;
  };

  // Helper function to load Google Identity Services script
  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait a bit for Google to initialize
        setTimeout(() => resolve(), 100);
      };
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  };

  const loginWithGithub = async () => {
    try {
      dispatch({ type: 'AUTH_START' });
      // TODO: Implement GitHub OAuth integration
      dispatch({ type: 'AUTH_FAILURE', payload: 'GitHub login not implemented yet' });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'GitHub login failed' });
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Remove auth cookie
      document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value: AuthContextType = {
    state,
    login,
    signup,
    logout,
    loginWithGoogle,
    loginWithGithub,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
